import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { TelegramWriteAccess } from "../../../plugins/telegram_write_access.js";
import { BotGiftService } from "../../../bot_gift/bot_gift_service.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Easing } from "../../../transforms/transform_base.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIGradientOverlay } from "../../common_ui_elements/ui_gradient_overlay.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { TooltipManager } from "../../tooltip/tooltip_manager.js";
import { EventLockedTooltip } from "../../tooltip/event_locked_tooltip.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { SafeAreas } from "../../safe_areas.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { MoreLivesDialog } from "../../dialog/more_lives_dialog.js";
import { explainMissingCampaignLevel } from "../../../level_pack/level_gate.js";
import { PlayDialog, PlayDialogDifficulty, PlayDialogState } from "../../dialog/play_dialog.js";
import { getLevelDifficulty, hasCampaignLevel } from "../../../level_config.js";
import { refreshPlayerFromServer, refreshPlayerFromServerIfStale } from "../../../api/player_sync.js";
import { OfflineEconomy } from "../../../offline/offline_economy.js";
import { CurrencyBar } from "../../common_ui_elements/currency_bar.js";
import { PlayerAvatar } from "../../common_ui_elements/player_avatar.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import type { AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { EventsConfigService, GameEventID } from "../../../events_config/events_config_service.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { NotificationBadge } from "../../common_ui_elements/notification_badge.js";
import { ConveyorTutorial, ConveyorTutorialStep } from "../../../conveyor/conveyor_tutorial.js";
import type { ConveyorTutorialOverlayParams } from "../../overlay/overlays/conveyor_tutorial_overlay.js";
import type { FirstStepsStateDto, FirstStepsTaskDto } from "../../../api/dto/reward_widgets.dto.js";
import { TelegramAPISettings } from "../../../telegram_api_settings.js";
import { AppConfigService } from "../../../app_config/app_config_service.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { LevelBoxDialog } from "../../dialog/level_box_dialog.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";

const MENU_DATA_COOLDOWN_MS = 0;

type RewardWidgetKey = "daily_cards" | "wheel" | "level_box" | "happy_fall";

interface RewardWidgetView
{
  holder: UIImage;
  button: UIImage;
  text: UITextElement;
  badge: NotificationBadge;
  shown: string;
  is_claim: boolean | null;
}

export class MenuScreen extends UIElement
{
  public static active: MenuScreen | null = null;
  private static readonly CONVEYOR_TUTORIAL_IDLE_MS = 600;
  private static readonly WIDGET_BADGE_SIZE = 56;

  private events_controller!: BasicEventsController;

  private is_driving = false;

  private clouds: { el: UIImage; speed: number; w: number }[] = [];
  private cloud_wrap_w = 0;
  private clouds_last_now = 0;

  private level: number;

  private currency_bar: CurrencyBar | null = null;
  private conveyor_widget?: UIImage;
  private conveyor_image?: UIImage;
  private conveyor_button?: UIImage;
  private conveyor_lock?: UIImage;
  private conveyor_lock_shown = false;
  private conveyor_tutorial_idle_since = 0;
  private first_steps_widget?: UIImage;
  private first_steps_badge?: NotificationBadge;
  private first_steps_text?: UITextElement;
  private first_steps_shown: boolean | null = null;
  private first_steps_claimable: boolean | null = null;
  private first_steps_recheck = false;
  private event_widget_states: Partial<Record<GameEventID, "hidden" | "locked" | "unlocked">> = {};
  private event_locked_tooltip: EventLockedTooltip | null = null;
  private play_button?: UIImage;
  private avatar: PlayerAvatar | null = null;
  private next_ui_sync_at = 0;
  private play_check_inflight = false;
  private readonly feature_ended_queue: Array<{ overlay_id: OverlayId; rewards: AdventureBoxReward[] }> = [];
  private readonly reward_widget_views: Partial<Record<RewardWidgetKey, RewardWidgetView>> = {};
  private feature_ended_overlay_open = false;

  private readonly on_window_focus = () =>
  {
    void refreshPlayerFromServerIfStale( MENU_DATA_COOLDOWN_MS );
    this.recheckFirstSteps();
  };
  private readonly on_visibility_change = () =>
  {
    if ( document.visibilityState === "visible" )
    {
      void refreshPlayerFromServerIfStale( MENU_DATA_COOLDOWN_MS );
      this.recheckFirstSteps();
    }
  };

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    MenuScreen.active = this;

    if ( ConveyorTutorial.getStep() !== ConveyorTutorialStep.None )
      ConveyorTutorial.setStep( ConveyorTutorialStep.None );

    this.initializeEventsController( ctx.canvas );

    const stats = StatisticsStorage.load();
    this.level = stats.level;
    // Menu isn't visited after every win anymore (winning goes straight into the next
    // level), so instead of a caller-supplied flag we detect "new level(s) since we last
    // showed the menu" ourselves, however many levels were skipped in between.
    const is_new_level = this.level > stats.last_menu_level;

    this.createBackground();

    const cart_level = is_new_level ? stats.last_menu_level : this.level;
    const difficulty = getLevelDifficulty( cart_level );

    this.createAnimal();

    this.createTopElements();
    this.createSettingsButton();
    this.createCardWidget();
    this.createBoxWidget();
    this.createWheelWidget();
    this.createConveyorWidget();
    this.createHappyFallWidget();
    this.createFirstStepsWidget();
    void RewardWidgetsService.refresh( MENU_DATA_COOLDOWN_MS ).then( () => this.syncRewardWidgets() );
    this.createButton();

    if ( is_new_level )
    {
      this.play_button?.setScaleImmediate( new Vector2( 0, 0 ) );
      this.play_button?.setClickable( false );
      this.animateNewLevel();
      StatisticsStorage.update( { last_menu_level: this.level } );
    }

    void refreshPlayerFromServerIfStale( MENU_DATA_COOLDOWN_MS );
    queueMicrotask( () => this.showStartupOverlays() );
    TelegramWriteAccess.maybeRequest( this.level, () => MenuScreen.active === this );
    window.addEventListener( "focus", this.on_window_focus );
    document.addEventListener( "visibilitychange", this.on_visibility_change );
  }

  private showStartupOverlays(): void
  {
    if ( MenuScreen.active !== this ) return;

    this.enqueueReturnGift();
    this.showNextFeatureEndedOverlay();
  }

  private enqueueReturnGift(): void
  {
    if ( BotGiftService.hasPending() )
      this.feature_ended_queue.push( { overlay_id: OverlayId.RETURN_GIFT_OVERLAY, rewards: [] } );
  }

  private showNextFeatureEndedOverlay(): void
  {
    if ( MenuScreen.active !== this || this.feature_ended_overlay_open ) return;

    const next = this.feature_ended_queue.shift();
    if ( !next ) return;

    this.feature_ended_overlay_open = true;
    const overlay = OverlayFactory.openOverlay( next.overlay_id, {
      rewards: next.rewards,
      on_closed: () =>
      {
        this.feature_ended_overlay_open = false;
        this.showNextFeatureEndedOverlay();
      },
    } );

    if ( !overlay )
    {
      this.feature_ended_overlay_open = false;
      this.showNextFeatureEndedOverlay();
    }
  }

  private createSettingsButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 100 );
    const h = LayoutScaler.heightFromDesign( 100 );
    const x = this.rect.getW() - w - LayoutScaler.xFromDesign( 40 );
    const y = LayoutScaler.yFromDesign( 123 );

    const btn = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_MenuSettingsIcon,
      {},
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      Game.instance.openSettingsDialog();
    } );
    this.addChild( btn );
  }

  private createCardWidget(): void
  {
    const bg_size = LayoutScaler.widthFromDesign( 210 );
    const bg_x = this.rect.getW() - bg_size - LayoutScaler.xFromDesign( 30 );
    const bg_y = LayoutScaler.yFromDesign( 300 );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_size,
      bg_size,
      ImgID.UI_MenuWidgetBgPurple,
      {},
      true
    )

    this.addChild( bg );

    const image_w = LayoutScaler.widthFromDesign( 190 );
    const image_h = LayoutScaler.heightFromDesign( 173 );
    const image_x = ( bg_size - image_w ) / 2;
    const image_y = LayoutScaler.yFromDesign( 12 );

    const image = new UIImage(
      this.ctx,
      image_x,
      image_y,
      image_w,
      image_h,
      ImgID.UI_DailyCardRewardWidget,
      {},
    );

    bg.addChild( image, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( bg_size - button_w ) / 2;
    const button_y = bg_size - button_h + LayoutScaler.yFromDesign( 10 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetBlueBtn,
      {},
    );

    bg.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_time,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    button.addChild( btn_text, true );

    const badge = this.createWidgetBadge( bg );
    this.reward_widget_views.daily_cards = { holder: bg, button, text: btn_text, badge, shown: "", is_claim: null };
    bg.setOnClick( () => this.openDailyCards() );
  }

  private createWheelWidget(): void
  {
    const bg_size = LayoutScaler.widthFromDesign( 210 );
    const bg_x = this.rect.getW() - bg_size - LayoutScaler.xFromDesign( 30 );
    const bg_y = LayoutScaler.yFromDesign( 300 ) + bg_size + LayoutScaler.heightFromDesign( 56 );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_size,
      bg_size,
      ImgID.UI_MenuWidgetBgPurple,
      {},
      true
    );

    this.addChild( bg );

    const image_w = LayoutScaler.widthFromDesign( 190 );
    const image_h = LayoutScaler.heightFromDesign( 169 );
    const image_x = ( bg_size - image_w ) / 2;
    const image_y = LayoutScaler.yFromDesign( 10 );

    const image = new UIImage(
      this.ctx,
      image_x,
      image_y,
      image_w,
      image_h,
      ImgID.UI_WheelOfFortuneWidget,
      {},
    );

    bg.addChild( image, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( bg_size - button_w ) / 2;
    const button_y = bg_size - button_h + LayoutScaler.yFromDesign( 10 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetBlueBtn,
      {},
    );

    bg.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_time,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    button.addChild( btn_text, true );

    const badge = this.createWidgetBadge( bg );
    this.reward_widget_views.wheel = { holder: bg, button, text: btn_text, badge, shown: "", is_claim: null };
    bg.setOnClick( () => this.openWheel() );
  }

  private createBoxWidget(): void
  {
    const bg_size = LayoutScaler.widthFromDesign( 210 );
    const bg_x = LayoutScaler.xFromDesign( 30 );
    const bg_y = LayoutScaler.yFromDesign( 300 );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_size,
      bg_size,
      ImgID.UI_MenuWidgetBgBlue,
      {},
      true
    );

    this.addChild( bg );

    const image_w = LayoutScaler.widthFromDesign( 190 );
    const image_h = LayoutScaler.heightFromDesign( 169 );
    const image_x = ( bg_size - image_w ) / 2;
    const image_y = LayoutScaler.yFromDesign( 6 );

    const image = new UIImage(
      this.ctx,
      image_x,
      image_y,
      image_w,
      image_h,
      ImgID.UI_GreenBox,
      {},
    );

    bg.addChild( image, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( bg_size - button_w ) / 2;
    const button_y = bg_size - button_h + LayoutScaler.yFromDesign( 10 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetBlueBtn,
      {},
    );

    bg.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_time,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    button.addChild( btn_text, true );

    const badge = this.createWidgetBadge( bg );
    this.reward_widget_views.level_box = { holder: bg, button, text: btn_text, badge, shown: "", is_claim: null };
    bg.setOnClick( () => this.openLevelBox() );
  }

  private createConveyorWidget(): void
  {
    const holder_size = LayoutScaler.widthFromDesign( 210 );
    const holder_x = LayoutScaler.xFromDesign( 30 );
    const holder_y = LayoutScaler.yFromDesign( 300 ) + ( holder_size + LayoutScaler.heightFromDesign( 56 ) ) * 2;

    const holder = new UIImage(
      this.ctx,
      holder_x,
      holder_y,
      holder_size,
      holder_size,
      ImgID.UI_MenuWidgetBgBlue,
      {},
      true
    );
    this.addChild( holder );

    const image_w = LayoutScaler.widthFromDesign( 190 );
    const image_h = LayoutScaler.heightFromDesign( 169 );
    const image_x = ( holder_size - image_w ) / 2;
    const image_y = LayoutScaler.yFromDesign( 10 );

    const image = new UIImage(
      this.ctx,
      image_x,
      image_y,
      image_w,
      image_h,
      ImgID.UI_ConveyorWidget,
      {},
    );
    image.setClickable( false );
    holder.addChild( image, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( holder_size - button_w ) / 2;
    const button_y = holder_size - button_h + LayoutScaler.yFromDesign( 10 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetGreenBtn,
      {},
    );
    button.setClickable( false );
    holder.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "PLAY",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_claim,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );

    button.addChild( btn_text, true );

    const lock_size = LayoutScaler.widthFromDesign( 140 );
    const lock_x = ( holder_size - lock_size ) / 2;
    const lock_y = ( holder_size - lock_size ) / 2;

    const lock = new UIImage( this.ctx, lock_x, lock_y, lock_size, lock_size, ImgID.IconNavigationLock );
    lock.setClickable( false );
    lock.setOpacityImmediate( 0 );
    holder.addChild( lock, true );

    this.conveyor_widget = holder;
    this.conveyor_image = image;
    this.conveyor_button = button;
    this.conveyor_lock = lock;
    holder.setOnClick( () => this.openConveyor() );
  }

  private createHappyFallWidget(): void
  {
    const holder_size = LayoutScaler.widthFromDesign( 210 );
    const holder_x = LayoutScaler.xFromDesign( 30 );
    const holder_y = LayoutScaler.yFromDesign( 300 ) + holder_size + LayoutScaler.heightFromDesign( 56 );

    const holder = new UIImage(
      this.ctx,
      holder_x,
      holder_y,
      holder_size,
      holder_size,
      ImgID.UI_MenuWidgetBgBlue,
      {},
      true
    );
    this.addChild( holder );

    const image_w = LayoutScaler.widthFromDesign( 190 );
    const image_h = LayoutScaler.heightFromDesign( 169 );
    const image_x = ( holder_size - image_w ) / 2;
    const image_y = LayoutScaler.yFromDesign( 10 );

    const image = new UIImage(
      this.ctx,
      image_x,
      image_y,
      image_w,
      image_h,
      ImgID.UI_ConveyorWidget,
      {},
    );
    image.setClickable( false );
    holder.addChild( image, true );

    const logo_w = LayoutScaler.widthFromDesign( 220 );
    const logo_h = LayoutScaler.heightFromDesign( 102 );
    const logo_x = ( holder_size - logo_w ) / 2;
    const logo_y = image_y + ( image_h - logo_h ) / 2;

    const logo = new UIImage(
      this.ctx,
      logo_x,
      logo_y,
      logo_w,
      logo_h,
      ImgID.UI_HappyFallLogo,
      {},
    );
    logo.setClickable( false );
    holder.addChild( logo, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( holder_size - button_w ) / 2;
    const button_y = holder_size - button_h + LayoutScaler.yFromDesign( 15 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetGreenBtn,
      {},
    );
    button.setClickable( false );
    holder.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "PLAY",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_claim,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    button.addChild( btn_text, true );

    const badge = this.createWidgetBadge( holder );
    this.reward_widget_views.happy_fall = { holder, button, text: btn_text, badge, shown: "", is_claim: null };
    holder.setOnClick( () => this.openHappyFall() );
  }

  private createFirstStepsWidget(): void
  {
    const holder_size = LayoutScaler.widthFromDesign( 210 );
    const holder_x = this.rect.getW() - holder_size - LayoutScaler.xFromDesign( 30 );
    const holder_y = LayoutScaler.yFromDesign( 300 ) + ( holder_size + LayoutScaler.heightFromDesign( 56 ) ) * 2;

    const holder = new UIImage(
      this.ctx,
      holder_x,
      holder_y,
      holder_size,
      holder_size,
      ImgID.UI_MenuWidgetBgBlue,
      {},
      true
    );
    this.addChild( holder );

    const logo_w = LayoutScaler.widthFromDesign( 196 );
    const logo_h = LayoutScaler.heightFromDesign( 163 );
    const logo_x = ( holder_size - logo_w ) / 2;
    const logo_y = LayoutScaler.yFromDesign( 13 );

    const logo = new UIImage(
      this.ctx,
      logo_x,
      logo_y,
      logo_w,
      logo_h,
      ImgID.UI_FirstStepsLogo,
      {},
    );
    logo.setClickable( false );
    holder.addChild( logo, true );

    const button_w = LayoutScaler.widthFromDesign( 220 );
    const button_h = LayoutScaler.heightFromDesign( 90 );
    const button_x = ( holder_size - button_w ) / 2;
    const button_y = holder_size - button_h + LayoutScaler.yFromDesign( 10 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuWidgetGreenBtn,
      {},
    );
    button.setClickable( false );
    holder.addChild( button, true );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "MENU.GO",
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 700,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        outline_color: ColorScheme.widget_claim,
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    button.addChild( btn_text, true );

    holder.setClickable( false );
    holder.hideElement( 0 );
    holder.setOnClick( () => this.openFirstSteps() );
    this.first_steps_widget = holder;
    this.first_steps_text = btn_text;
    this.first_steps_badge = this.createWidgetBadge( holder );
  }

  private createWidgetBadge( holder: UIImage ): NotificationBadge
  {
    const size = LayoutScaler.widthFromDesign( MenuScreen.WIDGET_BADGE_SIZE );
    const radius = holder.getRect().getW() / 2;
    const offset = radius * Math.SQRT1_2;
    const x = radius + offset - size / 2;
    const y = radius - offset - size / 2;

    const badge = new NotificationBadge( this.ctx, x, y, size, size );
    badge.setOpacityImmediate( 0 );
    holder.addChild( badge, true );
    return badge;
  }

  private setWidgetBadge( holder: UIImage | undefined, badge: NotificationBadge | undefined, visible: boolean ): void
  {
    badge?.setOpacityImmediate( visible ? 1 : 0 );
    holder?.forceRedraw();
  }

  private syncFirstStepsWidget(): void
  {
    const state = RewardWidgetsService.firstSteps();
    const shown = state !== null;
    const claimable = state?.claimable ?? false;

    if ( shown !== this.first_steps_shown )
    {
      this.first_steps_shown = shown;
      this.first_steps_widget?.setClickable( shown );
      if ( shown )
        this.first_steps_widget?.showElement( 0 );
      else
        this.first_steps_widget?.hideElement( 0 );
    }

    if ( claimable !== this.first_steps_claimable )
    {
      this.first_steps_claimable = claimable;
      this.first_steps_text?.setLocalizationKey( claimable ? "CLAIM" : "MENU.GO" );
      this.setWidgetBadge( this.first_steps_widget, this.first_steps_badge, claimable );
    }
  }

  private openFirstSteps(): void
  {
    const state = RewardWidgetsService.firstSteps();
    if ( !state )
    {
      void RewardWidgetsService.refresh().then( () => this.syncRewardWidgets() );
      return;
    }

    OverlayFactory.openOverlay( OverlayId.FIRST_STEPS_OVERLAY, {
      state,
      on_go: ( task: FirstStepsTaskDto ) => this.goToFirstStepsTask( task, state ),
      on_claimed: () => this.syncRewardWidgets(),
    } );
  }

  public goToFirstStepsTask( task: FirstStepsTaskDto, state: FirstStepsStateDto ): void
  {
    switch ( task.metric )
    {
      case "start_bot":
        if ( state.bot_url )
        {
          this.first_steps_recheck = true;
          TelegramAPISettings.openLink( state.bot_url );
        }
        break;
      case "join_group":
        this.openCommunityForFirstSteps();
        break;
      case "invite_friends":
        void ScreenManager.showScreen( Screens.FRIENDS );
        break;
      case "spin_wheel":
        this.openWheel();
        break;
      case "reach_level":
      case "use_boosters":
      case "earn_coins":
      case "spend_coins":
        this.play();
        break;
      default:
        break;
    }
  }

  private openCommunityForFirstSteps(): void
  {
    const url = AppConfigService.get().community_url;
    if ( !url )
      return;

    this.first_steps_recheck = true;
    TelegramAPISettings.openLink( url );
  }

  private recheckFirstSteps(): void
  {
    if ( !this.first_steps_recheck )
      return;

    this.first_steps_recheck = false;
    void RewardWidgetsService.refresh().then( () => this.syncRewardWidgets() );
  }

  private openHappyFall(): void
  {
    const refill_at = RewardWidgetsService.happyFallRefillAt();
    if ( refill_at === null )
    {
      void ScreenManager.showScreen( Screens.HAPPY_FALL );
      return;
    }

    OverlayFactory.openOverlay( OverlayId.HAPPY_FALL_WAIT_OVERLAY, { refill_at } );
  }

  private syncConveyorLock(): void
  {
    const locked = this.event_widget_states.conveyor === "locked";
    if ( locked === this.conveyor_lock_shown )
      return;

    this.conveyor_lock_shown = locked;
    this.conveyor_image?.setDimmed( locked );
    this.conveyor_button?.setOpacityImmediate( locked ? 0 : 1 );
    this.conveyor_lock?.setOpacityImmediate( locked ? 1 : 0 );
    this.conveyor_widget?.forceRedraw();
  }

  private openConveyor(): void
  {
    if ( !EventsConfigService.isEventUnlocked( "conveyor" ) )
    {
      if ( this.conveyor_widget )
        this.showEventLockedTooltip( this.conveyor_widget, EventsConfigService.getUnlockLevel( "conveyor" ) );
      return;
    }

    void ScreenManager.showScreen( Screens.GAME, { is_conveyor: true } );
  }

  private maybeStartConveyorTutorial( now: number ): void
  {
    const idle = ConveyorTutorial.getStep() === ConveyorTutorialStep.None
      && this.event_widget_states.conveyor === "unlocked"
      && !OverlayManager.isAnyOverlayOpened()
      && !DialogManager.isAnyDialogOpened()
      && !TooltipManager.isAnyTooltipOpened()
      && ConveyorTutorial.isPending();

    if ( !idle )
    {
      this.conveyor_tutorial_idle_since = 0;
      return;
    }

    if ( this.conveyor_tutorial_idle_since === 0 )
      this.conveyor_tutorial_idle_since = now;

    if ( now - this.conveyor_tutorial_idle_since >= MenuScreen.CONVEYOR_TUTORIAL_IDLE_MS )
      this.startConveyorTutorial();
  }

  public startConveyorTutorial(): void
  {
    const widget = this.conveyor_widget;
    if ( !widget )
      return;

    this.conveyor_tutorial_idle_since = 0;
    ConveyorTutorial.setStep( ConveyorTutorialStep.Widget );

    const params: ConveyorTutorialOverlayParams = {
      target: widget.getAbsoluteRect(),
      on_tap: () =>
      {
        ConveyorTutorial.setStep( ConveyorTutorialStep.Play );
        this.openConveyor();
      },
    };
    OverlayFactory.openOverlay( OverlayId.CONVEYOR_TUTORIAL_OVERLAY, params );
  }

  private openDailyCards(): void
  {
    const cards = RewardWidgetsService.dailyCards();
    if ( !cards )
    {
      void RewardWidgetsService.refresh().then( () => this.syncRewardWidgets() );
      return;
    }

    OverlayFactory.openOverlay( OverlayId.DAILY_CARD_REWARD_OVERLAY, { rewarded: cards.available } );
  }

  private openWheel(): void
  {
    const wheel = RewardWidgetsService.wheel();
    if ( !wheel )
    {
      void RewardWidgetsService.refresh().then( () => this.syncRewardWidgets() );
      return;
    }

    OverlayFactory.openOverlay( OverlayId.WHEEL_OF_FORTUNE_OVERLAY, { rewarded: wheel.available } );
  }

  private openLevelBox(): void
  {
    const box = RewardWidgetsService.levelBox();
    if ( !box )
    {
      void RewardWidgetsService.refresh().then( () => this.syncRewardWidgets() );
      return;
    }

    if ( box.available )
      OverlayFactory.openOverlay( OverlayId.LEVEL_BOX_OPEN_OVERLAY );
    else
      DialogManager.openDialog( new LevelBoxDialog( box.next_box_level ) );
  }

  private syncRewardWidgets(): void
  {
    if ( !OverlayManager.isInteractionBlocked() )
      RewardWidgetsService.applyClaimedRewards();

    const now_ms = Date.now();
    const cards = RewardWidgetsService.dailyCards( now_ms );
    const wheel = RewardWidgetsService.wheel( now_ms );
    const box = RewardWidgetsService.levelBox();
    const fall = RewardWidgetsService.happyFall( now_ms );

    if ( cards )
      this.syncRewardWidget( "daily_cards", cards.available, this.timerLabel( cards.next_available_at, now_ms ) );
    if ( wheel )
      this.syncRewardWidget( "wheel", wheel.available, this.timerLabel( wheel.next_available_at, now_ms ) );
    if ( box )
      this.syncRewardWidget( "level_box", box.available, `${box.levels_done}/${box.every_levels}` );
    if ( fall )
      this.syncRewardWidget( "happy_fall", fall.available, this.timerLabel( fall.next_available_at, now_ms ), "PLAY" );

    this.syncFirstStepsWidget();
  }

  private timerLabel( next_available_at: string | null, now_ms: number ): string
  {
    const left_ms = RewardWidgetsService.nextMs( next_available_at ) - now_ms;
    return formatShortDuration( Math.max( 0, left_ms ) );
  }

  private syncRewardWidget( key: RewardWidgetKey, is_claim: boolean, waiting_text: string, claim_key = "CLAIM" ): void
  {
    const view = this.reward_widget_views[key];
    if ( !view )
      return;

    const text = is_claim ? LocalizationManager.t( claim_key ) : waiting_text;
    if ( view.is_claim !== is_claim )
    {
      view.is_claim = is_claim;
      const outline_width = LayoutScaler.widthFromDesign( 8 );
      const outline_color = is_claim ? ColorScheme.widget_claim : ColorScheme.widget_time;
      view.button.setImageId( is_claim ? ImgID.UI_MenuWidgetGreenBtn : ImgID.UI_MenuWidgetBlueBtn );
      view.text.setOutline( outline_width, outline_color );
      this.setWidgetBadge( view.holder, view.badge, is_claim );
    }

    if ( view.shown !== text )
    {
      view.shown = text;
      view.text.setText( text );
    }
  }

  private syncEventUnlocks(): void
  {
    this.syncEventWidget( "conveyor", this.conveyor_widget );
    this.syncConveyorLock();
  }

  private syncEventWidget( id: GameEventID, widget: UIImage | undefined ): void
  {
    const config = EventsConfigService.get()[id];
    const state = !EventsConfigService.isLoaded() || !config.enabled ? "hidden"
      : EventsConfigService.isEventUnlocked( id ) ? "unlocked" : "locked";
    if ( state === this.event_widget_states[id] ) return;
    this.event_widget_states[id] = state;
    this.hideEventLockedTooltip();

    if ( !widget ) return;

    if ( state === "hidden" )
    {
      widget.setClickable( false );
      widget.hideElement( 0 );
      return;
    }

    widget.setDimmed( state === "locked" );
    widget.setClickable( true );
    widget.showElement( 0 );
  }

  private showEventLockedTooltip( widget: UIImage, unlock_level: number ): void
  {
    this.hideEventLockedTooltip();

    const widget_rect = widget.getAbsoluteRect();
    const menu_canvas = this.ctx.canvas;
    const tooltip_canvas = TooltipManager.ctx.canvas;
    const offset_x = parseFloat( menu_canvas.style.left || "0" ) - parseFloat( tooltip_canvas.style.left || "0" );
    const offset_y = parseFloat( menu_canvas.style.top || "0" ) - parseFloat( tooltip_canvas.style.top || "0" );
    const anchor_center_x = offset_x + widget_rect.x + widget_rect.width / 2;
    const anchor_top = offset_y + widget_rect.y;

    const tooltip = EventLockedTooltip.create(
      TooltipManager.ctx,
      anchor_center_x,
      anchor_top,
      this.rect.getW(),
      unlock_level,
      () => { this.event_locked_tooltip = null; }
    );

    this.event_locked_tooltip = tooltip;
    TooltipManager.openTooltip( tooltip );
  }

  private hideEventLockedTooltip(): void
  {
    if ( this.event_locked_tooltip )
      TooltipManager.closeTooltip();
  }

  private static readonly BG_DESIGN_W = 1855;
  private static readonly BG_DESIGN_H = 1855;

  private static readonly BG_LIFT_Y = 275;
  private static readonly BG_SKY_COVER_RATIO = 0.5;
  private static readonly BG_CLOUDS_RATIO = 0.3;

  private createClouds( sky_w: number, sky_h: number ): void
  {
    const max_cloud_w = LayoutScaler.widthFromDesign( 436 );
    const max_cloud_h = LayoutScaler.heightFromDesign( 280 );
    const layout_scale = LayoutScaler.getScale();

    this.cloud_wrap_w = sky_w;

    for ( let i = 0; i < 7; i++ )
    {
      const scale = 0.6 + Math.random() * 0.4;

      const cloud_w = max_cloud_w * scale;
      const cloud_h = max_cloud_h * scale;

      const cloud = new UIImage(
        this.ctx,
        Math.random() * ( sky_w - cloud_w ),
        Math.random() * Math.max( 0, sky_h - cloud_h ),
        cloud_w,
        cloud_h,
        ImgID.UI_MenuCloud
      );

      this.addChild( cloud );

      const cloud_speed = ( 10 + ( 1 - scale ) * 55 + Math.random() * 18 ) * layout_scale;
      this.clouds.push( { el: cloud, speed: cloud_speed, w: cloud_w } );
    }
  }

  private createBackground(): void
  {
    const bg_img = ImagesHolder.getImage( ImgID.UI_MenuBg );

    const screen_w = this.rect.getW();
    const screen_h = this.rect.getH();

    // cover: беремо більший коефіцієнт, щоб картинка гарантовано перекрила екран
    const design_w = LayoutScaler.widthFromDesign( MenuScreen.BG_DESIGN_W );
    const design_h = LayoutScaler.heightFromDesign( MenuScreen.BG_DESIGN_H );

    const bg_w = design_w;
    const bg_h = design_h;

    // низ картинки там само, де був низ площі, тобто під нижніми табами
    const image_h = bg_h;
    const image_w = bg_w;
    const image_x = ( screen_w - image_w ) / 2;
    const image_y = this.rect.getH() - image_h - LayoutScaler.heightFromDesign( MenuScreen.BG_LIFT_Y );

    // ---- небо ----

    const sky_h = image_y + image_h * MenuScreen.BG_SKY_COVER_RATIO + Game.safeAreaTop();

    const sky = new UIGradientOverlay(
      this.ctx,
      0,
      -Game.safeAreaTop(),
      screen_w,
      sky_h,
      "#0067F3",
      "#E5FCFF"
    );

    this.addChild( sky );

    this.createClouds( screen_w, image_y + image_h * MenuScreen.BG_CLOUDS_RATIO + Game.safeAreaTop() );

    const background = new UIImage( this.ctx, image_x, image_y, image_w, image_h, ImgID.UI_MenuBg );
    this.addChild( background );
  }

  public static getCoinIconCenter(): Vector2
  {
    return CurrencyBar.defaultCoinCenter();
  }

  private createTopElements(): void
  {
    this.createAvatar();

    const position = CurrencyBar.defaultPosition();

    this.currency_bar = new CurrencyBar( this.ctx, position.x, position.y, { clickable: true, show_plus: true } );
    this.addChild( this.currency_bar );
  }

  private createAvatar(): void
  {
    const position = PlayerAvatar.defaultPosition();
    const size = PlayerAvatar.defaultSize();

    this.avatar = new PlayerAvatar( this.ctx, position.x, position.y, size, { clickable: true } );
    this.addChild( this.avatar );
  }

  private static readonly NEW_LEVEL_BUTTON_DELAY_MS = 250;

  private animateNewLevel(): void
  {
    this.revealPlayButton( MenuScreen.NEW_LEVEL_BUTTON_DELAY_MS );
  }

  // Debug helper: drive the current cart off-screen and roll a fresh one in
  // showing the SAME level (no level change) — for previewing the drive animation.
  // public replayCartDrive(): void
  // {
  //   this.driveCarts( this.new_cart ?? this.cart, this.level );
  // }

  // private driveCarts( outgoing: UICart, incoming_level: number, reveal_play_button = false ): void
  // {
  //   if ( this.is_driving )
  //     return;

  //   this.is_driving = true;

  //   this.new_cart = undefined;
  //   this.is_driving = false;

  //   if ( reveal_play_button )
  //     this.revealPlayButton();
  // }

  // Pop the Play button in with the same scale-overshoot used for buttons
  // revealed inside dialogs (e.g. PlayDialog's close button).
  private revealPlayButton( delay_ms = 0 ): void
  {
    const btn = this.play_button;
    if ( !btn )
      return;

    const now = performance.now() + delay_ms;
    const pop_up_duration = 176;
    const pop_settle_duration = 112;

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 0, 0 ), new Vector2( 1.25, 1.25 ),
      now, pop_up_duration,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 1.25, 1.25 ), new Vector2( 1.0, 1.0 ),
      now + pop_up_duration, pop_settle_duration,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.setClickable( true );
  }

  private static readonly ANIMAL_H = 690;
  private static readonly ANIMAL_W = 690;
  private static readonly ANIMAL_OFFSET_Y = 620;
  private static readonly ANIMALS: ReadonlyArray<ImgID> = [
    ImgID.UI_MenuAnimal1,
    ImgID.UI_MenuAnimal2,
    ImgID.UI_MenuAnimal3,
    ImgID.UI_MenuAnimal4,
    ImgID.UI_MenuAnimal5,
    ImgID.UI_MenuAnimal6,
    ImgID.UI_MenuAnimal7,
    ImgID.UI_MenuAnimal8,
  ];

  private static animal_id: ImgID | null = null;
  private static animal_level = -1;

  private static animalForLevel( level: number ): ImgID
  {
    if ( MenuScreen.animal_id !== null && MenuScreen.animal_level === level )
      return MenuScreen.animal_id;

    const options = MenuScreen.ANIMALS.filter( id => id !== MenuScreen.animal_id );
    MenuScreen.animal_id = options[ Math.floor( Math.random() * options.length ) ];
    MenuScreen.animal_level = level;
    return MenuScreen.animal_id;
  }

  private createAnimal(): void
  {
    const h = LayoutScaler.heightFromDesign( MenuScreen.ANIMAL_H );
    const w = LayoutScaler.widthFromDesign( MenuScreen.ANIMAL_W );
    const x = this.rect.getW2() - w / 2;
    const y = this.rect.getH() - h - LayoutScaler.yFromDesign( MenuScreen.ANIMAL_OFFSET_Y );
    const animal_id = MenuScreen.animalForLevel( this.level );

    const animal = new UIImage( this.ctx, x, y, w, h, animal_id );
    animal.setClickable( false );
    this.addChild( animal );
  }

  private createButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 700 );
    const h = LayoutScaler.heightFromDesign( 280 );
    const x = this.rect.getW() / 2 - w / 2;
    const y = this.rect.getH() - h - LayoutScaler.yFromDesign( 380 );

    const difficulty = getLevelDifficulty( this.level );
    const button_img_id =
      difficulty === PlayDialogDifficulty.SuperHard ? ImgID.UI_RedBtn
      : difficulty === PlayDialogDifficulty.Hard ? ImgID.UI_PurpleFrameBtn
      : ImgID.UI_PlayButton;

    const button = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      button_img_id,
      {},
      true
    );

    button.setOnClick( () => this.play() );

    this.addChild( button );
    this.play_button = button;

    const is_difficult = difficulty !== PlayDialogDifficulty.Common;
    const text_style = {
      font_weight: 800,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      scale_to_width: true,
      auto_wrap: false,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: difficulty === PlayDialogDifficulty.Common ? ColorScheme.menu_button_outline : difficulty === PlayDialogDifficulty.Hard ? ColorScheme.menu_button_outline_hard : ColorScheme.menu_button_outline_superhard,
      line_height: 1.5
    };

    const play_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 70 ),
      LayoutScaler.yFromDesign( 5 ),
      w - LayoutScaler.widthFromDesign( 140 ),
      LayoutScaler.heightFromDesign( 284 ),
      {
        localization_key: "MENU_PLAY",
        font_size: LayoutScaler.textSizeFromDesign( 62 ),
        text_variable_params: [ this.level.toString() ],
        ...text_style,
      }
    );
    button.addChild( play_text, true );

    if ( is_difficult )
    {
      const header_w = LayoutScaler.widthFromDesign( 467 );
      const header_h = LayoutScaler.heightFromDesign( 108 );

      const header_x = w / 2 - header_w / 2;
      const header_y = LayoutScaler.yFromDesign( -88 );

      const header = new UIImage(
        this.ctx,
        header_x,
        header_y,
        header_w,
        header_h,
        ImgID.UI_PlayButtonHeader,
        {}
      );

      button.addChild( header, true );

      const txt = difficulty === PlayDialogDifficulty.SuperHard ? "Super Hard!" : "Hard!";

      const text_h = LayoutScaler.heightFromDesign( 80 );
      const text_y = LayoutScaler.yFromDesign( 23 );

      const text = new UITextElement(
        this.ctx,
        0,
        text_y,
        header_w,
        text_h,
        {
          text: txt,
          font_size: LayoutScaler.textSizeFromDesign( 60 ),
          font_weight: 900,
          text_color: ColorScheme.menu_button_hard,
          text_style: TextStyleScheme.rubik,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.white,
          shadow_offset_x: 0,
          shadow_offset_y: 0
        }
      );
      
      header.addChild( text, true );
    }
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );

    this.events_controller.init(
      ( p: Vector2 ) => super.onTouchStart( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchEnd( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchMove( p, performance.now() )
    );
  }

  public destroy(): void
  {
    if ( MenuScreen.active === this )
      MenuScreen.active = null;

    window.removeEventListener( "focus", this.on_window_focus );
    document.removeEventListener( "visibilitychange", this.on_visibility_change );

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null as any;
    }
  }

  private play(): void
  {
    if ( this.play_check_inflight )
      return;

    if ( !hasCampaignLevel( this.level ) )
    {
      explainMissingCampaignLevel( this.level );
      return;
    }

    if ( StatisticsStorage.load().hearts <= 0 )
    {
      DialogManager.openDialog( new MoreLivesDialog() );
      return;
    }

    void audioController.playSound( AudioClips.SEARCH_LEVEL );
    this.play_check_inflight = true;
    void this.verifyHeartsAndPlay();
  }

  private async verifyHeartsAndPlay(): Promise<void>
  {
    try
    {
      // Booster counts are server-owned. Wait for the shared refresh instead
      // of opening the play dialog with the inventory's initial zero values.
      await refreshPlayerFromServer();

      this.level = StatisticsStorage.load().level;
      if ( !hasCampaignLevel( this.level ) )
      {
        await ScreenManager.showScreen( Screens.MENU );
        explainMissingCampaignLevel( this.level );
        return;
      }

      if ( StatisticsStorage.load().hearts <= 0 )
      {
        await ScreenManager.showScreen( Screens.MENU );
        DialogManager.openDialog( new MoreLivesDialog() );
        return;
      }

      await ScreenManager.showScreen( Screens.MENU );
      DialogManager.openDialog( new PlayDialog( this.level, PlayDialogState.Play, getLevelDifficulty( this.level ) ) );
    }
    catch ( error )
    {
      await ScreenManager.showScreen( Screens.MENU );
      console.error( "[MenuScreen] Failed to prepare game", error );
    }
    finally
    {
      this.play_check_inflight = false;
    }
  }

  private syncTopBar(): void
  {
    const stats = StatisticsStorage.load();
    const now_ms = Date.now();

    this.currency_bar?.sync();

    this.avatar?.sync();

    if ( stats.hearts < stats.max_hearts
      && stats.next_heart_at > 0
      && now_ms >= stats.next_heart_at )
      OfflineEconomy.applyHeartsRegen( now_ms );
  }

  public name(): string
  {
    return "MenuScreen";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public update( now: number ): void
  {
    this.updateClouds( now );

    if ( now >= this.next_ui_sync_at )
    {
      this.next_ui_sync_at = now + 1000;
      this.syncTopBar();
      this.syncEventUnlocks();
      this.syncRewardWidgets();
    }

    this.maybeStartConveyorTutorial( now );
    this.updateBasic( now );
  }

  private paint_shift_accum = 0;

  private requestPaintForShift( shift: number ): void
  {
    this.paint_shift_accum += Math.abs( shift );

    // A dialog dims this canvas but does not replace it. Keep producing full
    // menu frames while it is open so animated clouds remain live underneath
    // the translucent overlay instead of depending on the normal paint
    // threshold/dirty propagation.
    if ( DialogManager.isAnyDialogOpened() )
    {
      this.paint_shift_accum = 0;
      this.invalidateSubtree();
      return;
    }

    if ( this.paint_shift_accum < 1 / ( window.devicePixelRatio || 1 ) )
      return;

    this.paint_shift_accum = 0;
    this.setShouldBeRendered();
  }

  private updateClouds( now: number ): void
  {
    if ( this.clouds.length === 0 )
      return;

    const dt = this.clouds_last_now > 0 ? Math.min( 0.05, ( now - this.clouds_last_now ) / 1000 ) : 0;
    this.clouds_last_now = now;

    if ( dt === 0 )
      return;

    let max_move = 0;

    for ( const c of this.clouds )
    {
      const pos = c.el.pixelPosImportant();
      const movement = c.speed * dt;
      let x = pos.x + movement;

      if ( x > this.cloud_wrap_w )
        x = -c.w;

      c.el.setPixelPos( new Vector2( x, pos.y ), true );
      max_move = Math.max( max_move, Math.abs( movement ) );
    }

    this.requestPaintForShift( max_move );
  }
}
